/* eslint-disable no-mixed-spaces-and-tabs */
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
	collection,
	doc,
	getDoc,
	getDocs,
	query,
	where,
	Timestamp,
	setDoc,
	updateDoc,
	addDoc,
	deleteDoc,
} from 'firebase/firestore';
import { firestore, storage } from '@/utils/firebase';
import BASE_QUERY from '@/features/baseQuery';
import { getActingUser } from '../user/userApi';
import {
	ref,
	getBlob,
	uploadBytes,
	getDownloadURL,
	deleteObject,
	listAll,
} from 'firebase/storage';
import { flattenToNested, formatCurrency, formatDate, toTitleCase } from '@/utils/constants';
import { clientsApi } from '../clients/clientsApi';
import { dealsApi } from '../deals/dealsApi';
import { chunk } from 'lodash';
import Handlebars from '@/utils/handlebars';
import {
	EMAIL_TEMPLATE_BLOCKS_COLLECTION,
	EMAIL_TEMPLATES_COLLECTION,
	extractBlockTypes,
	formatDataForScopeOfAdvice,
	getDealHtmlValue,
	safelyMergeObjects,
	TEMPLATE_GROUPS,
	AI_HTML_TEMPLATES_COLLECTION,
} from './helper';
import { STORAGE_PURPOSES, brokerStoragePath, templateStoragePath } from '@/utils/storagePaths';
import testDealJson from '@/assets/json/test-deal.json';
import testClientJson from '@/assets/json/test-client.json';
import testSOAJson from '@/assets/json/test-soa.json';
import testPreRenewalJson from '@/assets/json/test-pre-renewal.json';
import {
	assignBlockIds,
	applySignatureStyles,
	FIELDS,
	DEAL,
	CLAIM,
	parseHtmlAndConvertStyles,
} from '@/utils/emailHelper';
import { ticketsApi } from '../claims/ticketsApi';
import { CLIENT_TYPE, COMMERCIAL } from '@/components/forms/CreateNewBusinessForm/validationSchema';
import { classesOfRiskApi } from '../classesOfRisk/classesOfRiskApi';

// Creating an API service using RTK Query
export const templatesApi = createApi({
	reducerPath: 'templatesApi',
	tagTypes: [
		'EMAIL_TEMPLATES',
		'EMAIL_TEMPLATE',
		'EMAIL_TEMPLATE_BLOCKS',
		'AI_HTML_TEMPLATES',
		'AI_HTML_TEMPLATE',
	],
	baseQuery: fetchBaseQuery(BASE_QUERY),
	endpoints: (builder) => ({
		getEmailTemplates: builder.query({
			providesTags: (result, error, arg) =>
				arg?.type ? [{ type: 'EMAIL_TEMPLATES', id: arg.type }] : ['EMAIL_TEMPLATES'],
			queryFn: async (params, { getState, dispatch }) => {
				try {
					console.log('Get email templates params', params);
					const email = getState()?.msalAccount?.account?.username;
					if (!email) {
						return { data: [] };
					}

					const userData = await getActingUser(dispatch, getState);

					const { userId, brokerGroupArray } = userData ?? {};

					const collectionRef = collection(firestore, EMAIL_TEMPLATES_COLLECTION);

					// Only read templates this user can see, instead of the whole collection:
					// master, their own, shared with one of their broker groups, or shared with
					// them by name. Merged by doc id. With ownerDocId, only that owner's.
					const visibilityQueries = params?.ownerDocId
						? [query(collectionRef, where('ownerDocId', '==', params.ownerDocId))]
						: [
								query(collectionRef, where('master', '==', true)),
								query(collectionRef, where('ownerDocId', '==', userId)),
								...chunk(brokerGroupArray ?? [], 30).map((ids) =>
									query(
										collectionRef,
										where('brokerGroupIds', 'array-contains-any', ids),
									),
								),
								query(
									collectionRef,
									where(
										'users',
										'array-contains',
										doc(firestore, 'user', userId),
									),
								),
							];

					const snapshots = await Promise.all(visibilityQueries.map((q) => getDocs(q)));
					const docsById = new Map(
						snapshots.flatMap((snapshot) => snapshot.docs).map((d) => [d.id, d]),
					);

					// type and dealId are applied here rather than in the queries, so the
					// queries need no extra composite indexes.
					const templateDocs = [...docsById.values()].filter(
						(d) =>
							(!params?.type || d.data().type === params.type) &&
							(!params?.dealId || d.data().dealId === params.dealId),
					);

					const aiTemplatesByType = templateDocs
						.filter((doc) => {
							return (
								doc.data().aiGenerated &&
								(!params?.dealId || params?.dealId === doc.data().dealId)
							);
						})
						.reduce((acc, doc) => {
							const splitName = doc
								.data()
								.name.toLowerCase()
								.split('email template generated');
							const type = doc.data().generatedEmailType ?? splitName?.[0]?.trim();
							const dealId = doc.data().dealId;
							if (!dealId) return acc;

							const createdAt = doc.data().createdWhen?.toMillis?.() ?? 0;

							acc[dealId] ??= {};
							const existing = acc[dealId][type];

							if (
								!existing ||
								createdAt > (existing.data()?.createdWhen?.toMillis?.() ?? 0)
							) {
								acc[dealId][type] = doc;
							}

							return acc;
						}, {});

					const aiTemplates = Object.values(aiTemplatesByType).reduce(
						(acc, val) => [...acc, ...Object.values(val)],
						[],
					);

					const docs = [
						...templateDocs.filter((d) => !d.data().aiGenerated),
						...aiTemplates,
					]
						.map((doc) => {
							const userIds = (doc.data().users ?? []).map((v) => v.id);
							const brokerGroupIds = (doc.data().brokerGroups ?? []).map((v) => v.id);
							const ownerDocId = doc.data().ownerDocId;
							const dealId = doc.data().dealId;
							const master = doc.data().master;

							return {
								userIds,
								brokerGroupIds,
								path: doc.data().path ?? '',
								master,
								canUpdate: doc.data().canUpdate ?? !master,
								ownerDocId,
								type: doc.data().type,
								id: doc.id,
								name: doc.data().name ?? toTitleCase(doc.data().type ?? ''),
								dealId,
								createdWhen: doc.data().createdWhen?.toMillis?.() ?? 0,
								lastUpdated: doc.data().lastUpdated?.toMillis?.() ?? 0,
								requirements: doc.data().requirements ?? [],
								aiGenerated: doc.data().aiGenerated ?? false,
								sortBy: master
									? TEMPLATE_GROUPS.master
									: doc.data().aiGenerated
										? TEMPLATE_GROUPS.generated
										: ownerDocId === userId
											? TEMPLATE_GROUPS.user
											: TEMPLATE_GROUPS.shared,
							};
						})
						.filter(
							(d) =>
								d.master ||
								d.ownerDocId === userId ||
								d.brokerGroupIds.some((r) => brokerGroupArray.includes(r)) ||
								d.userIds.includes(userId),
						);

					console.log('Email templates response', docs);
					return { data: docs };
				} catch (error) {
					console.error('❌ Error retrieving email templates', error);
					return { error };
				}
			},
		}),
		getAIHtmlTemplates: builder.query({
			providesTags: ['AI_HTML_TEMPLATES'],
			// providesTags: (result, error, arg) =>
			// 	arg?.type ? [{ type: 'AI_HTML_TEMPLATES', id: arg.type }] : ['EMAIL_TEMPLATES'],
			queryFn: async (params, { getState, dispatch }) => {
				try {
					console.log('Get AI html templates params', params);

					const userData = await getActingUser(dispatch, getState);

					const { userId, brokerGroupArray } = userData ?? {};

					const collectionRef = collection(firestore, AI_HTML_TEMPLATES_COLLECTION);

					const constraints = [
						...(params?.type ? [where('type', '==', params.type)] : []),
						...(params?.ownerDocId
							? [where('ownerDocId', '==', params.ownerDocId)]
							: []),
					];

					const q = query(collectionRef, ...constraints);
					const querySnapshot = await getDocs(q);

					// Filter before downloading any HTML: only fetch templates this user can see
					// (the Storage rules refuse the others).
					const visibleDocs = params.showAdmin
						? querySnapshot.docs
						: querySnapshot.docs.filter((doc) => {
								const { users, brokerGroups, master, ownerDocId } = doc.data();
								return (
									master ||
									ownerDocId === userId ||
									(brokerGroups ?? []).some((r) =>
										(brokerGroupArray ?? []).includes(r.id),
									) ||
									(users ?? []).some((u) => u.id === userId)
								);
							});

					const response = await Promise.all(
						visibleDocs.map(async (doc) => {
							const userIds = (doc.data().users ?? []).map((v) => v.id);
							const brokerGroupIds = (doc.data().brokerGroups ?? []).map((v) => v.id);

							const data = JSON.parse(JSON.stringify(doc.data()));
							const html = await dispatch(
								templatesApi.endpoints.getAIHtmlTemplateHtml.initiate({
									id: doc.id,
									...(!doc.data().path && { html: doc.data().html }),
									path: doc.data().path,
								}),
							).unwrap();

							return {
								...data,
								html,
								userIds,
								brokerGroupIds,
								id: doc.id,
								// name: toTitleCase(doc.data().type ?? ''),
								createdWhen: doc.data().createdWhen?.toMillis?.() ?? 0,
								lastUpdated: doc.data().lastUpdated?.toMillis?.() ?? 0,
							};
						}),
					);

					console.log('AI html templates response', response);
					return { data: response };
				} catch (error) {
					console.error('❌ Error retrieving AI html templates', error);
					return { error };
				}
			},
		}),
		getEmailTemplateHtml: builder.query({
			providesTags: (result, error, arg) => [{ type: 'EMAIL_TEMPLATE', id: arg.id }],
			queryFn: async ({ id, path }, { getState, dispatch }) => {
				const getHtml = async (docId, filePath) => {
					console.log('[getEmailTemplateHtml params', { docId, filePath });
					if (!filePath || typeof filePath !== 'string' || filePath.trim() === '') {
						throw new Error(`Invalid filePath provided for email template ${docId}`);
					}
					const fileRef = ref(storage, filePath);

					try {
						const blob = await getBlob(fileRef);
						return await blob.text();
					} catch (err) {
						if (err.code === 'storage/object-not-found') {
							console.warn(
								`[getEmailTemplateHtml - getHtml] No template found at ${filePath}`,
							);
						}
						throw err;
					}
				};

				try {
					const text = await getHtml(id, path);

					return { data: text };
				} catch (error) {
					console.error(`❌ Error retrieving email template ${id}, retrying ...`, error);

					try {
						const docRef = doc(firestore, EMAIL_TEMPLATES_COLLECTION, id);
						const templateDoc = await getDoc(docRef);

						const text = await getHtml(id, templateDoc?.data()?.path);
						return { data: text };
					} catch (e) {
						console.error(`❌ Error retrieving email template after retry ${id}`, e);
						return { error: { code: e.code, message: e.message } };
					}
				}
			},
		}),
		getAIHtmlTemplateHtml: builder.query({
			providesTags: (result, error, arg) => [{ type: 'AI_HTML_TEMPLATE', id: arg.id }],
			queryFn: async ({ id, path, html }, { getState, dispatch }) => {
				const getHtml = async (docId, filePath) => {
					console.log('[getAIHtmlTemplateHtml params', { docId, filePath });
					if (!filePath || typeof filePath !== 'string' || filePath.trim() === '') {
						throw new Error(`Invalid filePath provided for email template ${docId}`);
					}
					const fileRef = ref(storage, filePath);

					try {
						const blob = await getBlob(fileRef);
						return await blob.text();
					} catch (err) {
						if (err.code === 'storage/object-not-found') {
							console.warn(
								`[getAIHtmlTemplateHtml - getHtml] No template found at ${filePath}`,
							);
						}
						throw err;
					}
				};

				if (html) {
					try {
						const parser = new DOMParser();
						const doc = parser.parseFromString(html, 'text/html');
						if (doc.body.children.length > 0) {
							return { data: html };
						}
					} catch (e) {
						console.error('Error getting html without filepath', e);
					}
				}

				try {
					const text = await getHtml(id, path);

					return { data: text };
				} catch (error) {
					console.error(
						`❌ Error retrieving AI html template ${id}, retrying ...`,
						error,
					);

					try {
						const docRef = doc(firestore, AI_HTML_TEMPLATES_COLLECTION, id);
						const templateDoc = await getDoc(docRef);

						const text = await getHtml(id, templateDoc?.data()?.path);
						return { data: text };
					} catch (e) {
						console.error(`❌ Error retrieving AI html template after retry ${id}`, e);
						return { error: { code: e.code, message: e.message } };
					}
				}
			},
		}),
		getEmailTemplateBlockByType: builder.query({
			providesTags: (result, error, arg) => [{ type: 'EMAIL_TEMPLATE_BLOCK', id: arg?.id }],
			queryFn: async ({ type }, { getState, dispatch }) => {
				try {
					console.log('Get email template block by id params', { type });

					const collectionRef = collection(firestore, EMAIL_TEMPLATE_BLOCKS_COLLECTION);

					const returnArray = type && typeof type === 'object' && Array.isArray(type);
					const queryDocs = [];
					if (typeof type === 'string') {
						const q = query(collectionRef, where('type', '==', type));
						const querySnapshot = await getDocs(q);
						queryDocs.push(...querySnapshot.docs);
					} else if (returnArray) {
						const chunks = chunk(type, 30);
						for (const typeChunks of chunks) {
							const q = query(collectionRef, where('type', 'in', typeChunks));
							const querySnapshot = await getDocs(q);
							queryDocs.push(...querySnapshot.docs);
						}
					}

					const docs = queryDocs.map((doc) => ({
						path: doc.data().path ?? '',
						type: doc.data().type,
						id: doc.id,
						name: doc.data().name ?? toTitleCase(doc.data().type ?? ''),
						createdWhen: doc.data().createdWhen?.toMillis?.() ?? 0,
						lastUpdated: doc.data().lastUpdated?.toMillis?.() ?? 0,
					}));

					for (const blockDoc of docs) {
						const html = await dispatch(
							templatesApi.endpoints.getEmailTemplateHtml.initiate({
								id: blockDoc?.id,
								path: blockDoc?.path,
							}),
						).unwrap();
						console.log('[getEmailTemplateBlockByType] html', html);
						blockDoc.html = html;
					}

					console.log('Email template block by id response', docs);
					return {
						data: returnArray ? docs : docs[0],
					};
				} catch (error) {
					console.error('❌ Error retrieving email template block by id', error);
					return { error };
				}
			},
		}),
		getParsedEmailTemplate: builder.query({
			providesTags: (result, error, arg) => [
				{
					type: 'EMAIL_TEMPLATE',
					id: arg?.template?.id,
				},
			],
			queryFn: async ({ template, clientId, dealId, isMarble }, { getState, dispatch }) => {
				try {
					console.log('Get email template block by id params', {
						template,
						clientId,
						dealId,
					});

					const htmlData =
						template?.id && template?.path
							? await dispatch(
									templatesApi.endpoints.getEmailTemplateHtml.initiate({
										id: template?.id,
										path: template?.path,
									}),
								).unwrap()
							: '';
					console.log('HTML DATA', htmlData);

					const userData = await getActingUser(dispatch, getState);

					const clientData = clientId
						? await dispatch(
								clientsApi.endpoints.getClientProperties.initiate(clientId),
							).unwrap()
						: {};

					const dealData = dealId
						? await dispatch(dealsApi.endpoints.getDealRow.initiate(dealId)).unwrap()
						: {};
					const claimData =
						dealId === testDealJson.id || dealData?.isClaim
							? await dispatch(
									ticketsApi.endpoints.getInsightClaim.initiate(dealId),
								).unwrap()
							: {};

					const fields = FIELDS.filter(
						(field) => field.type === DEAL || field.type === CLAIM,
					);

					let classesOfRisk = [];

					const brokerGroupIds = userData?.brokerGroupArray;

					const clientType = dealData?.[CLIENT_TYPE] ?? COMMERCIAL;
					const clientTypeTitleCase = clientType.replace(
						/\w\S*/g,
						(text) => text.charAt(0).toUpperCase() + text.substring(1).toLowerCase(),
					);
					classesOfRisk = await dispatch(
						classesOfRiskApi.endpoints.getClassesOfRisk.initiate({
							clientType,
							countryCode: userData?.broker?.country ?? 'NZ',
							isParent: false,
							brokerGroupIds,
						}),
					).unwrap();

					const dealRow = fields.reduce(
						(acc, field) => {
							try {
								acc[field.value] = getDealHtmlValue({
									dealRow: dealData,
									field,
									classesOfRisk,
								});
							} catch (e) {
								console.error(
									`Error getting field value for deal row ${field.value}`,
									e,
								);
								acc[field.value] = '';
							}

							return acc;
						},
						{ ...dealData },
					);
					// console.log('parsedEmailTemplate deal', { dealRow, dealData });

					const preRenewalTemplate =
						template?.type === 'preRenewal' && dealId
							? await dispatch(
									templatesApi.endpoints.getPreRenewalEmailTemplateData.initiate({
										dealId,
									}),
								).unwrap()
							: null;

					const soaTemplate =
						template?.type === 'statementOfAdvice' && dealId
							? await dispatch(
									templatesApi.endpoints.getSOAEmailTemplateData.initiate({
										dealId,
									}),
								).unwrap()
							: null;

					const clientRow = clientData?.clientRow;

					const blockTypes = extractBlockTypes(htmlData);
					console.log('[getParsedEmailTemplate] Block types', blockTypes);

					const blockData =
						blockTypes.length > 0
							? await dispatch(
									templatesApi.endpoints.getEmailTemplateBlockByType.initiate({
										type: blockTypes,
									}),
								).unwrap()
							: [];
					console.log('[getParsedEmailTemplate] Block data', blockData);

					console.log('Datas', { soaTemplate, preRenewalTemplate });
					const getTemplateData = (blockHtmlObj = {}) => {
						const data = {
							...blockHtmlObj,
							client: {
								...(clientRow ?? {}),
								...(blockHtmlObj?.client ?? {}),
								clientType: clientTypeTitleCase,
							},
							deal: {
								...(dealRow ?? {}),
								...(blockHtmlObj?.deal ?? {}),
								clientType: clientTypeTitleCase,
							},
							claim: {
								...(dealRow ?? {}),
								...(blockHtmlObj?.deal ?? {}),
								...(blockHtmlObj?.claim ?? {}),
								clientType: clientTypeTitleCase,
							},
							broker: {
								...(userData ?? {}),
								...(blockHtmlObj?.user ?? {}),
							},
							user: {
								...(userData ?? {}),
							},
							brokerage: {
								...(userData?.broker ?? {}),
								...(blockHtmlObj?.brokerage ?? {}),
							},
							insightClaim: {
								...(claimData ?? {}),
								...(blockHtmlObj?.insightClaim ?? {}),
							},
						};
						if (
							!template?.type ||
							!['statementOfAdvice', 'preRenewal', 'scopeOfAdvice'].includes(
								template.type,
							) ||
							Object.keys(clientData ?? {}).length === 0 ||
							Object.keys(dealRow ?? {}).length === 0 ||
							Object.keys(userData ?? {}).length === 0
						) {
							return data;
						}
						if (template?.type === 'statementOfAdvice') {
							const soaTemplateData = soaTemplate?.data ?? {};
							return safelyMergeObjects(data, soaTemplateData);
						} else if (template?.type === 'preRenewal') {
							const renewalTemplateData = preRenewalTemplate?.data ?? {};
							return safelyMergeObjects(data, renewalTemplateData);
						} else if (template?.type === 'scopeOfAdvice') {
							const soaData =
								formatDataForScopeOfAdvice({
									client: clientData,
									dealRow: dealData,
									userData,
									isMarble,
									classesOfRisk,
								}) ?? {};
							return safelyMergeObjects(data, soaData);
						} else {
							return data;
						}
					};

					let templateData = getTemplateData();
					const blockHtml = blockData.reduce((acc, block) => {
						const template = Handlebars.compile(
							block.html.toString().replace(/>\s+</g, '><').replace(/\n/g, ''),
						);
						const html = template({
							...templateData,
						});
						acc[block.type] = html;
						return acc;
					}, {});

					const nestedBlockHtml = flattenToNested(blockHtml, '.');

					templateData = getTemplateData(nestedBlockHtml);

					return {
						data: templateData,
					};
				} catch (error) {
					console.error('❌ Error retrieving parsed email template', error);
					return { error };
				}
			},
		}),
		getEmailTemplateBlocks: builder.query({
			providesTags: (result, error, arg) => ['EMAIL_TEMPLATE_BLOCKS'],
			queryFn: async (params, { getState, dispatch }) => {
				try {
					console.log('Get email template blocks params', params);

					const collectionRef = collection(firestore, EMAIL_TEMPLATE_BLOCKS_COLLECTION);

					const q = query(collectionRef);
					const querySnapshot = await getDocs(q);

					const docs = querySnapshot.docs.map((doc) => ({
						path: doc.data().path ?? '',
						type: doc.data().type,
						html: doc.data().html,
						id: doc.id,
						name: doc.data().name ?? toTitleCase(doc.data().type ?? ''),
						createdWhen: doc.data().createdWhen?.toMillis?.() ?? 0,
						lastUpdated: doc.data().lastUpdated?.toMillis?.() ?? 0,
					}));

					console.log('Email template blocks response', docs);
					return { data: docs };
				} catch (error) {
					console.error('❌ Error retrieving email templates', error);
					return { error };
				}
			},
		}),
		getSOAEmailTemplateData: builder.query({
			// providesTags: (result, error, arg) => [{ type: 'EMAIL_TEMPLATE', id: arg.id }],
			queryFn: async (
				{ dealId, soaDocId },
				{ getState, dispatch },
				_extraOptions,
				baseQuery,
			) => {
				try {
					const isTest = dealId === testDealJson?.id;

					const response = isTest
						? { data: { ...testSOAJson } }
						: await baseQuery({
								url: process.env.REACT_APP_GET_SOA_EMAIL_DATA,
								body: { dealId, soaDocId },
								method: 'POST',
							});
					if (isTest) {
						const userData = await getActingUser(dispatch, getState);
						const email = getState()?.msalAccount?.account?.username;

						const brokerData = userData?.broker;

						const {
							ledger,
							name: brokerName,
							fullLogo,
							disclosureUrl,
							about: aboutBrokerage,
							fspNumber,
							colours,
							phoneNumber: brokeragePhone,
							website,
							contactEmail: brokerageEmail,
						} = brokerData ?? {};

						const userName = userData?.name ?? '';
						const signature = userData?.signature;

						return {
							data: {
								data: {
									...response.data,
									userName,
									...(signature
										? {
												signature: signature
													.toString()
													.replace(/>\s+</g, '><')
													.replace(/\n/g, ''),
											}
										: null),
									email,
									contactName: testClientJson?.clientRow?.contactName ?? '',
									clientName: testClientJson?.clientRow?.name ?? '',
									brokerName,
									...(fullLogo && { logoUrl: fullLogo }),
									...(colours && { colours }),
								},
							},
						};
					}

					return response;
				} catch (error) {
					console.error('❌ Error retrieving email templates', error);
					return { error };
				}
			},
			// query: ({ dealId, soaDocId }) => ({
			// 	url: process.env.REACT_APP_GET_SOA_EMAIL_DATA,
			// 	method: 'POST',
			// 	body: { dealId, soaDocId },
			// }),
			transformErrorResponse: (response) => {
				console.log('👾 ~ transform getSOAEmailTemplateData error response', response);
				return response;
			},
			transformResponse: (response) => {
				console.log('👾 ~ getSOAEmailTemplateData data', response);
				return response;
			},
		}),
		getPreRenewalEmailTemplateData: builder.query({
			// providesTags: (result, error, arg) => [{ type: 'EMAIL_TEMPLATE', id: arg.id }],
			queryFn: async (
				{ dealId },
				{ getState, dispatch },
				_extraOptions,
				baseQuery,
			) => {
				try {
					console.log('[getPreRenewalEmailTemplateData] DEAL ID', dealId);
					const isTest = dealId === testDealJson?.id;
					if (isTest) {
						const userData = await getActingUser(dispatch, getState);

						const brokerData = userData?.broker;

						const {
							ledger,
							name: brokerName,
							fullLogo,
							disclosureUrl,
							about: aboutBrokerage,
							fspNumber,
							colours,
							phoneNumber: brokeragePhone,
							website,
							contactEmail: brokerageEmail,
							privacyStatement,
							termsOfEngagement,
						} = brokerData ?? {};

						const userName = userData?.name ?? '';
						const signature = userData?.signature;

						const fspValue = (fspNumber ?? '').toString().toLowerCase().includes('fsp')
							? fspNumber
							: `FSP ${fspNumber}`;

						const { industry, numberOfEmployees, annualRevenue } =
							testClientJson?.clientRow ?? {};

						const dealData = testDealJson ?? {};

						const { clientType, description: dealDescription } = dealData;

						const description =
							testClientJson?.clientRow?.description ?? dealDescription;

						const clientTypeTitleCase = clientType?.replace(
							/\w\S*/g,
							(text) =>
								text.charAt(0).toUpperCase() + text.substring(1).toLowerCase(),
						);

						return {
							data: {
								data: {
									...testPreRenewalJson,
									logoUrl: fullLogo,
									brokerage: {
										name: brokerName,
										disclosureUrl,
										privacyStatement,
										termsOfEngagement,
										colours,
										website,
										...(fspNumber && { fspNumber: fspValue }),
										logoUrl: fullLogo,
										ledger,
									},
									contactName: testClientJson?.clientRow?.contactName,
									clientName: testClientJson?.clientRow?.name,
									clientType: clientTypeTitleCase,
									industry,
									description,
									numberOfEmployees,
									annualTurnover: annualRevenue
										? formatCurrency({ num: annualRevenue })
										: null,
									broker: {
										name: userName,
										...(signature
											? {
													signature: signature
														.toString()
														.replace(/>\s+</g, '><')
														.replace(/\n/g, ''),
												}
											: null),
									},
								},
							},
						};
					}

					const response = await baseQuery({
						url: process.env.REACT_APP_GET_PRE_RENEWAL_EMAIL_DATA,
						body: { dealId },
						method: 'POST',
					});
					return response;
				} catch (error) {
					console.error('❌ Error retrieving email templates', error);
					return { error };
				}
			},
			// query: ({ dealId }) => ({
			// 	url: process.env.REACT_APP_GET_PRE_RENEWAL_EMAIL_DATA,
			// 	method: 'POST',
			// 	body: { dealId },
			// }),
			transformErrorResponse: (response) => {
				console.log(
					'👾 ~ transform getPreRenewalEmailTemplateData error response',
					response,
				);
				return response;
			},
			transformResponse: (response) => {
				console.log('👾 ~ getPreRenewalEmailTemplateData data', response);
				return response;
			},
		}),
		getHandlebarsHtml: builder.query({
			// providesTags: (result, error, arg) => [{ type: 'EMAIL_TEMPLATE', id: arg.id }],
			queryFn: async (
				{ templateData, html: htmlString },
				{ getState, dispatch },
				_extraOptions,
				baseQuery,
			) => {
				try {
					const template = Handlebars.compile(
						htmlString.toString().replace(/>\s+</g, '><').replace(/\n/g, ''),
					);

					console.log('html string', htmlString, templateData);
					const safeRenewalDate = templateData?.renewalDate?.timeStamp
						? {
								...templateData.renewalDate,
								timeStamp: templateData.renewalDate.timeStamp.slice(0, 10),
							}
						: templateData?.renewalDate;

					const html = template({
						...templateData,
						...(safeRenewalDate && { renewalDate: safeRenewalDate }),
					});
					const newHtml = assignBlockIds(html.replace(/>\s+</g, '><').replace(/\n/g, ''));

					const parser = new DOMParser();
					const doc = parser.parseFromString(newHtml, 'text/html');
					// Work with the document safely
					const signatureNodes = [
						...doc.querySelectorAll('#email-signature'),
						...doc.querySelectorAll('[data-signature="true"]'),
					];

					console.log('signature', signatureNodes);
					const el = signatureNodes[0];
					if (el && el.tagName.toLowerCase() === 'div') {
						applySignatureStyles(el);
					}

					console.log(
						'getHandlebarsHtml doc.documentElement.outerHTML',
						doc.documentElement.outerHTML,
					);

					return { data: doc.documentElement.outerHTML };
				} catch (error) {
					console.error('❌ Error retrieving email templates', error);
					return { error };
				}
			},
			transformErrorResponse: (response) => {
				console.log(
					'👾 ~ transform getPreRenewalEmailTemplateData error response',
					response,
				);
				return response;
			},
			transformResponse: (response) => {
				console.log('👾 ~ getPreRenewalEmailTemplateData data', response);
				return response;
			},
		}),
		saveEmailTemplate: builder.mutation({
			invalidatesTags: (result, error, arg) => [
				'EMAIL_TEMPLATES',
				...(arg?.docId ? [{ type: 'EMAIL_TEMPLATE', id: arg.docId }] : []),
			],
			queryFn: async (
				{ type, html, name, userIds = [], brokerGroupIds = [], requirements = [], docId },
				{ getState, dispatch },
			) => {
				try {
					console.log('Save email templates params', {
						type,
						html,
						name,
						userIds,
						brokerGroupIds,
						requirements,
						docId,
					});

					if (docId) {
						const docRef = doc(firestore, EMAIL_TEMPLATES_COLLECTION, docId);
						const templateDoc = await getDoc(docRef);

						if (templateDoc?.data()?.master) {
							const data = templateDoc?.data();
							const returnedBody = {
								...data,
								lastUpdated: data.lastUpdated?.toMillis() ?? 0,
								brokerGroups: [],
								users: [],
							};
							return { data: returnedBody, docId };
						}

						const updatedBody = await dispatch(
							templatesApi.endpoints.saveEmailTemplateProperty.initiate({
								docId,
								properties: {
									type,
									html,
									name,
									userIds,
									brokerGroupIds,
									requirements,
								},
							}),
						).unwrap();

						console.log('UPDATED BODY', updatedBody);
						const returnedBody = {
							...updatedBody,
							brokerGroups: (updatedBody.brokerGroups ?? []).map((v) =>
								typeof v === 'string' ? v : v?.id,
							),
							users: (updatedBody.users ?? []).map((v) =>
								typeof v === 'string' ? v : v?.id,
							),
						};
						return { data: { ...returnedBody, docId } };
					}

					const userData = await getActingUser(dispatch, getState);

					const { userId, brokerGroupId } = userData ?? {};

					// Reserve the doc id first: the file is named after it (renaming the
					// template then never moves the file) and carries it in its metadata.
					const docRef = doc(collection(firestore, EMAIL_TEMPLATES_COLLECTION));
					docId = docRef.id;

					const filePath = brokerStoragePath(
						brokerGroupId,
						STORAGE_PURPOSES.emailTemplates,
						`${docId}.html`,
					);

					const storageRef = ref(storage, filePath);
					const metadata = {
						customMetadata: {
							'User ID': userId,
							'Email Template ID': docId,
						},
					};

					const htmlBlob = new Blob([html], { type: 'text/html' });
					const uploadedFile = await uploadBytes(storageRef, htmlBlob, metadata);

					const downloadUrl = await getDownloadURL(uploadedFile.ref);

					const brokerGroups = brokerGroupIds.map((id) =>
						doc(firestore, 'brokerGroup', id),
					);
					const users = Array.from(new Set([...userIds, userId])).map((id) =>
						doc(firestore, 'user', id),
					);
					const today = new Date();
					const createBody = {
						createdBy: doc(firestore, 'user', userId),
						ownerDocId: userId,
						brokerGroups,
						// brokerGroupIds (plain ids) is what the Storage rule reads.
						brokerGroupIds,
						html: downloadUrl,
						users,
						path: filePath,
						requirements,
						type,
						name,
						master: false,
						lastUpdated: Timestamp.fromDate(today),
					};

					try {
						await setDoc(docRef, {
							...createBody,
							createdWhen: Timestamp.fromDate(today),
						});
						console.log(`Successfully created email template doc ${docId}`);
					} catch (e) {
						console.log('Error setting doc', e);
						return { error: e };
					}

					console.log('Email templates response', downloadUrl);
					const returnedBody = {
						...createBody,
						createdBy: userId,
						createdWhen: today.getMilliseconds(),
						lastUpdated: today.getMilliseconds(),
						brokerGroups: (createBody.brokerGroups ?? []).map((v) =>
							typeof v === 'string' ? v : v?.id,
						),
						users: (createBody.users ?? []).map((v) =>
							typeof v === 'string' ? v : v?.id,
						),
					};
					return { data: { ...returnedBody, docId } };
				} catch (error) {
					console.error('❌ Error saving email template', error);
					return { error };
				}
			},
		}),
		saveEmailTemplateProperty: builder.mutation({
			invalidatesTags: (result, error, arg) => [
				'EMAIL_TEMPLATES',
				...(arg?.docId ? [{ type: 'EMAIL_TEMPLATE', id: arg.docId }] : []),
			],
			queryFn: async ({ docId, properties = {} }, { getState, dispatch }) => {
				try {
					console.log('Save email templates params', {
						docId,
						properties,
					});

					const docRef = doc(firestore, EMAIL_TEMPLATES_COLLECTION, docId);
					const templateDoc = await getDoc(docRef);

					if (templateDoc?.data()?.master) {
						const data = templateDoc?.data();
						const returnedBody = {
							...data,
							lastUpdated: data.lastUpdated?.toMillis() ?? 0,
							brokerGroups: [],
							users: [],
						};
						return { data: returnedBody, docId };
					}

					const userData = await getActingUser(dispatch, getState);

					const { userId } = userData ?? {};

					// The template keeps its owner when someone else edits it. Older template
					// docs have no ownerDocId, only the createdBy user ref.
					const templateData = templateDoc?.data();
					const ownerDocId = templateData?.ownerDocId ?? templateData?.createdBy?.id;

					const existingName = templateDoc?.data()?.name;
					const nameChanged = properties.name && properties.name !== existingName;

					// The file is named after the doc id, so a rename only changes Firestore;
					// new HTML overwrites the same file.
					if (properties.html) {
						const storageRef = ref(storage, templateDoc?.data()?.path);
						const metadata = {
							customMetadata: {
								'User ID': ownerDocId,
								'Email Template ID': docId,
							},
						};
						const htmlBlob = new Blob([properties.html], { type: 'text/html' });
						const uploadedFile = await uploadBytes(storageRef, htmlBlob, metadata);
						properties.downloadUrl = await getDownloadURL(uploadedFile.ref);
					}

					const brokerGroups = (properties.brokerGroupIds ?? []).map((id) =>
						doc(firestore, 'brokerGroup', id),
					);
					const users = Array.from(new Set([...(properties.userIds ?? []), userId])).map(
						(id) => doc(firestore, 'user', id),
					);

					const today = new Date();
					const createBody = {
						// brokerGroupIds (plain ids) is what the Storage rule reads.
						...(properties.brokerGroupIds && {
							brokerGroups,
							brokerGroupIds: properties.brokerGroupIds,
						}),
						...(properties.downloadUrl && { html: properties.downloadUrl }),
						...(properties.userIds && { users }),
						...(properties.requirements && { requirements: properties.requirements }),
						...(properties.type && { type: properties.type }),
						...(nameChanged && { name: properties.name }),

						lastUpdated: Timestamp.fromDate(today),
					};

					try {
						await updateDoc(docRef, {
							...createBody,
						}).then((res) =>
							console.log(`Successfully updated email template doc ${docId}`, res),
						);
					} catch (e) {
						console.log('Error setting doc', e);
						return { error: e };
					}

					console.log('Email templates response', createBody);
					const returnedBody = {
						...createBody,
						createdBy: createBody.createdBy?.id,
						lastUpdated: createBody.lastUpdated?.toMillis() ?? 0,
						brokerGroups: (createBody.brokerGroups ?? []).map((v) =>
							typeof v === 'string' ? v : v?.id,
						),
						users: (createBody.users ?? []).map((v) =>
							typeof v === 'string' ? v : v?.id,
						),
					};

					return { data: { ...returnedBody, docId } };
				} catch (error) {
					console.error(`❌ Error updating email template ${docId}`, error);
					return { error };
				}
			},
		}),
		deleteEmailTemplate: builder.mutation({
			invalidatesTags: (result, error, arg) => [
				'EMAIL_TEMPLATES',
				...(arg?.docId ? [{ type: 'EMAIL_TEMPLATE', id: arg.docId }] : []),
			],
			queryFn: async ({ docId }, { getState, dispatch }) => {
				try {
					console.log('Delete email template params', docId);

					const docRef = doc(firestore, EMAIL_TEMPLATES_COLLECTION, docId);
					const templateDoc = await getDoc(docRef);

					try {
						const fileRef = ref(storage, templateDoc?.data()?.path);
						await deleteObject(fileRef).then((res) =>
							console.log('Successfully deleted file'),
						);
					} catch (e) {
						console.log('Error deleting object', e);
						if (e.code === 'storage/object-not-found') {
							console.warn(
								`[deleteEmailTemplate] No template found at ${
									templateDoc?.data()?.path
								}`,
							);
						} else {
							return { error: { code: e.code, message: e.message } };
						}
					}
					await deleteDoc(docRef).then((res) =>
						console.log(`Successfully deleted email template doc ${docId}`, res),
					);
					return { data: { message: 'Document deleted' } };
				} catch (error) {
					console.error(`❌ Error deleting email template ${docId}`, error);
					return { error };
				}
			},
		}),
		saveAIHtmlTemplate: builder.mutation({
			invalidatesTags: (result, error, arg) => [
				'AI_HTML_TEMPLATES',
				...(arg?.docId ? [{ type: 'AI_HTML_TEMPLATE', id: arg.docId }] : []),
			],
			queryFn: async ({ docId: paramDocId, properties }, { getState, dispatch }) => {
				try {
					let docId = paramDocId;
					console.log('Save AI html template params', {
						docId,
						properties,
					});

					const userData = await getActingUser(dispatch, getState);

					const { userId, brokerGroupArray } = userData ?? {};

					const lastUpdated = Timestamp.now();
					const collectionRef = collection(firestore, AI_HTML_TEMPLATES_COLLECTION);
					if (!docId) {
						await addDoc(collectionRef, {
							html: properties.html,
							type: 'emailTemplate',
							ownerDocId: userId,
							master: false,
							brokerGroups: [],
							brokerGroupIds: [],
							// The creator is listed so the Storage rule lets them write the file.
							users: [doc(firestore, 'user', userId)],
							createdWhen: lastUpdated,
							lastUpdated,
							active: false,
							path: '',
							name: properties.name ?? 'New Template',
						}).then(async (res) => {
							console.log(`Successfully created AI html template doc ${res.id}`);
							docId = res.id;
						});
					}

					const docRef = doc(firestore, AI_HTML_TEMPLATES_COLLECTION, docId);
					const templateDoc = await getDoc(docRef);
					const data = templateDoc?.data();

					if (properties.html) {
						// Each save is a new version in the template's own folder.
						const newPath = templateStoragePath(
							'aiHtmlTemplates',
							docId,
							`${Date.now()}.html`,
						);

						let { downloadUrl } = data ?? {};

						const storageRef = ref(storage, newPath);
						const metadata = {
							customMetadata: {
								'User ID': userId.toString(),
								...(docId && { 'Template ID': docId }),
							},
						};
						const htmlBlob = new Blob([properties.html], { type: 'text/html' });
						const uploadedFile = await uploadBytes(storageRef, htmlBlob, metadata);
						downloadUrl = await getDownloadURL(uploadedFile.ref);

						properties.downloadUrl = downloadUrl;
						properties.path = newPath;
					}

					const brokerGroups = (properties.brokerGroupIds ?? []).map((id) =>
						doc(firestore, 'brokerGroup', id),
					);
					const users = Array.from(new Set([...(properties.userIds ?? []), userId])).map(
						(id) => doc(firestore, 'user', id),
					);

					const createBody = {
						lastUpdatedBy: userId,
						active: properties.active ?? false,
						lastUpdated,
						// brokerGroupIds (plain ids) is what the Storage rule reads.
						...(properties.brokerGroupIds && {
							brokerGroups,
							brokerGroupIds: properties.brokerGroupIds,
						}),
						...(properties.downloadUrl && { html: properties.downloadUrl }),
						...(properties.userIds && { users }),
						...(properties.path && { path: properties.path }),
						...(properties.name && { name: properties.name }),
					};

					try {
						await updateDoc(docRef, {
							...createBody,
						}).then((res) =>
							console.log(`Successfully updated email template doc ${docId}`, res),
						);
					} catch (e) {
						console.log('Error setting doc', e);
						return { error: e };
					}

					console.log('Email templates response', createBody);

					return { data: docId };
				} catch (error) {
					console.error(`❌ Error updating AI html template ${paramDocId}`, error);
					return { error };
				}
			},
		}),
		deleteAIHtmlTemplate: builder.mutation({
			invalidatesTags: (result, error, arg) => [
				'AI_HTML_TEMPLATES',
				...(arg?.docId ? [{ type: 'AI_HTML_TEMPLATE', id: arg.docId }] : []),
			],
			queryFn: async ({ docId }, { getState, dispatch }) => {
				try {
					console.log('Delete AI html template params', docId);

					const docRef = doc(firestore, AI_HTML_TEMPLATES_COLLECTION, docId);

					// The template's folder holds every saved version; delete them all.
					try {
						const { items } = await listAll(
							ref(storage, templateStoragePath('aiHtmlTemplates', docId)),
						);
						await Promise.all(items.map((item) => deleteObject(item)));
						console.log(`Deleted ${items.length} AI html template versions`);
					} catch (e) {
						console.log('Error deleting AI html template versions', e);
						return { error: { code: e.code, message: e.message } };
					}
					await deleteDoc(docRef).then((res) =>
						console.log(`Successfully deleted AI html template doc ${docId}`, res),
					);
					return { data: { message: 'Document deleted' } };
				} catch (error) {
					console.error(`❌ Error deleting AI html template ${docId}`, error);
					return { error };
				}
			},
		}),
	}),
});

// Exporting auto-generated hooks for the defined endpoints
export const {
	useGetEmailTemplatesQuery,
	useGetEmailTemplateHtmlQuery,
	useGetAIHtmlTemplatesQuery,
	useGetAIHtmlTemplateHtmlQuery,
	useGetSOAEmailTemplateDataQuery,
	useLazyGetSOAEmailTemplateDataQuery,
	useGetPreRenewalEmailTemplateDataQuery,
	useLazyGetPreRenewalEmailTemplateDataQuery,
	useGetParsedEmailTemplateQuery,
	useGetHandlebarsHtmlQuery,
	useLazyGetHandlebarsHtmlQuery,
	useGetEmailTemplateBlockByTypeQuery,
	useLazyGetEmailTemplateBlockByTypeQuery,
	useGetEmailTemplateBlocksQuery,
	useSaveEmailTemplateMutation,
	useSaveEmailTemplatePropertyMutation,
	useDeleteEmailTemplateMutation,
	useSaveAIHtmlTemplateMutation,
	useDeleteAIHtmlTemplateMutation,
} = templatesApi;
